import React from 'react';
import { useEffect, useState } from 'react';
import { AnyIfEmpty, useDispatch } from 'react-redux';
import iconClosePreview from '../../../images/icon-close-preview.svg';
import iconClose from '../../../images/icon-close.svg';
import iconUpload from '../../../images/icon-upload-unit.svg';
import { getLocationById } from '../../../redux/location/actions/location.action';
import { AppDispatch } from '../../../redux/rootReducer';
import { addUnits, editUnits, uploadImages } from '../../../redux/units/actions/units.action';
import Counter from '../../common/counter/Counter';
import Loader from '../../common/loader/Loader';
import styles from './AddUnits.module.css';
import TextArea from '../../common/input/textArea/TextArea';
import { useForm, Controller } from 'react-hook-form';
import TextInput from '../../common/input/textInput/TextInput';
import { unitsData } from '../unitscard/Unitscard';
import { trimData } from '../../../utils/trim-data';
import Select from 'react-select';
import { colourStyles } from '../../../constants/constants';

export enum UnitStatus {
    VACANT = 'vacant',
    OCCUPIED = 'occupied',
}

/** Dropdown shows "VACANT" / "OCCUPIED"; payload sends "available" for VACANT */
const UNIT_STATUS_OPTIONS = [
    { label: 'VACANT', value: 'available' },
    { label: 'OCCUPIED', value: 'occupied' },
] as const;
interface propsAddUnits {
    unitData?: AnyIfEmpty<object>;
    setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    locationId?: AnyIfEmpty<object>;
    setShowToast?: React.Dispatch<React.SetStateAction<boolean>>;
    setToastMessage?: React.Dispatch<React.SetStateAction<string>>;
    unitsData?: AnyIfEmpty<object>;
    setUnitsData?: AnyIfEmpty<object>;
    setCurrentPage?: React.Dispatch<React.SetStateAction<number>> | undefined;
    isLoading?: boolean;
    setToastType?: React.Dispatch<React.SetStateAction<string>>;
    onUnitAdded?: () => void;
}

interface UnitFormValues {
    unitName: string;
    nickName: string;
    description: string;
    size: number;
    beds: number;
    baths: number;
    price: number;
    uploadedImages: File[] | null;
    existingImages: string[];
    image_url: string[];
    status: boolean;
    buildingName: string | null;
    unit_status: string;
}

interface UnitPayload {
    name: string;
    nickName: string;
    description: string;
    size: number;
    rooms: number;
    baths: number;
    price: number;
    image_url: string;
    location: string;
    status: boolean;
    unitOccupancyStatus?: string;
    unitLeasedStatus?: string;
    buildingName: string | null;
    unit_status?: string;
}

const AddUnits = ({
    unitData,
    setOpen,
    locationId,
    setShowToast,
    setToastMessage,
    unitsData,
    setUnitsData,
    setCurrentPage,
    isLoading = false,
    setToastType,
    onUnitAdded,
}: propsAddUnits) => {
    const dispatch: AppDispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
        watch,
        setValue,
        setError,
        getValues,
    } = useForm<UnitFormValues>({
        defaultValues: {
            unitName: '',
            nickName: '',
            description: '',
            size: 0,
            beds: 0,
            baths: 0,
            price: 0,
            existingImages: [],
            uploadedImages: null,
            image_url: [],
            status: true,
            buildingName: null,
            unit_status: "",
        },
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    useEffect(() => {
        if (unitData && unitData?.id && locationId) {
            const imageArray =
                unitData.image_url && typeof unitData.image_url === 'string' ? unitData.image_url.split(',').filter(Boolean) : [];
            reset({
                unitName: unitData.unitYardiId || unitData.name || '',
                nickName: unitData.nickName || '',
                description: unitData.description || '',
                size: unitData.size || 0,
                beds: unitData.rooms || 0,
                baths: unitData.baths || '',
                existingImages: imageArray || [],
                uploadedImages: [],
                price: unitData.price || 0,
                image_url: imageArray || [],
                status: unitData.status || null,
                buildingName: unitData.buildingName || null,
                unit_status: unitData.unit_status || '',
            });
        }
    }, [unitData]);

    const closeModal = () => {
        if (setOpen) setOpen(false);
    };

    const unitStatusOptions = [...UNIT_STATUS_OPTIONS];

    const uploadedFiles = watch('uploadedImages') || [];
    const existingFiles = watch('existingImages') || [];

    const addUnitsSuccess = (message: string) => {
        if (setShowToast && setToastType && setToastMessage) {
            setShowToast(true);
            setToastType('success');
            setToastMessage(message);
        }
        // Refetch units list after successful addition
        onUnitAdded?.();
    };

    const addUnitsFailure = (message: string) => {
        if (setShowToast && setToastMessage && setToastType) {
            setShowToast(true);
            setToastMessage(message);
            setToastType('error');
        }
    };

    const editUnitsSuccess = (message: string) => {
        if (setShowToast && setToastType && setToastMessage) {
            setShowToast(true);
            setToastType('success');
            setToastMessage(message);
        }
        // Refetch units list after successful edit
        onUnitAdded?.();
    };

    const editUnitsFailure = (message: string) => {
        if (setShowToast && setToastType && setToastMessage) {
            setShowToast(true);
            setToastType('error');
            setToastMessage(message);
        }
    };
    const submitHandler = async (data: UnitFormValues) => {
        try {
            setLoading(true);
            const uploadedImages = data.uploadedImages ?? [];
            const existingImages = data.existingImages ?? [];

            let uploadedUrls: string[] = [];
            if (uploadedImages.length > 0) {
                uploadedUrls = await uploadImages(uploadedImages);
            }

            const finalImages = [...existingImages, ...uploadedUrls];
            const trim = (val: string) => trimData<string>(val) || '';
            const payload: UnitPayload = {
                name: trim(data.unitName),
                nickName: trim(data.nickName),
                description: trim(data.description),
                size: data.size,
                rooms: Number(data.beds),
                baths: Number(data.baths),
                price: data.price,
                image_url: finalImages.join(','),
                location: locationId,
                status: data.status,
                buildingName: data.buildingName,
            };
            if (unitData) {
                payload.unit_status = data.unit_status;
                await dispatch(editUnits(unitData.id, payload, editUnitsSuccess, editUnitsFailure));

                // Update unitsData manually so UI reflects it
                if (unitsData?.length > 0) {
                    const unitIndex = unitsData.findIndex((unit: unitsData) => unit.id === unitData.id);
                    if (unitIndex !== -1) {
                        const updatedUnits = [...unitsData];
                        updatedUnits[unitIndex] = {
                            ...unitsData[unitIndex],
                            ...payload,
                            unit_status: data.unit_status,
                            id: unitData.id,
                        };
                        setUnitsData?.(updatedUnits);
                    }
                }
            } else {
                await dispatch(
                    addUnits(
                        {
                            ...payload,
                            unitOccupancyStatus: 'Vacant',
                            unitLeasedStatus: 'Available',
                        },
                        setUnitsData,
                        setCurrentPage,
                        addUnitsSuccess,
                        addUnitsFailure,
                    ),
                );
            }
            setLoading(false);
            setOpen?.(false);
            dispatch(getLocationById(locationId));
        } catch (error: any) {
            console.error('Add/Edit unit error:', error);
            setToastMessage?.('Something went wrong');
            setShowToast?.(true);
            setToastType?.('error');
        } finally {
            setLoading(false);
        }
    };

    const handleTrimStartChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, onChange: (value: string) => void) => {
        const trimmedValue = e.target.value.trimStart();
        onChange(trimmedValue);
    };

    return (
        <div className={styles.modalAddStaff}>
            {(loading || isLoading) && <Loader />}

            <div className={styles.modalHeader}>
                <div className={styles.modalTitle}>
                    <h4>{unitData ? `Edit Unit` : `Add Unit`}</h4>
                </div>
                <span className={styles.close}>
                    <img src={iconClose} onClick={closeModal} />
                </span>
            </div>

            <div className={styles.modalBody}>
                <form onSubmit={handleSubmit(submitHandler)}>
                    <div className={styles.fieldWrapper}>
                        <Controller
                            name="unitName"
                            control={control}
                            rules={{
                                validate: (value) => {
                                    if (!value) return 'Apartment Number is required';
                                    if (value.trim() === '') return 'Empty space is not allowed';
                                    return true;
                                },
                            }}
                            render={({ field }) => (
                                <TextInput
                                    label="Apartment Number"
                                    isRequired={true}
                                    placeholder="Enter Apartment Number"
                                    {...field}
                                    onChange={(e) => handleTrimStartChange(e, field.onChange)}
                                    error={errors?.unitName?.message?.toString() || ''}
                                />
                            )}
                        />
                    </div>

                    <div className={styles.fieldWrapperAppNickName}>
                        <Controller
                            name="nickName"
                            control={control}
                            render={({ field }) => {
                                return (
                                    <TextInput
                                        label="Apartment Nickname"
                                        placeholder="Enter Apartment Nickname"
                                        // errorBorder={errors?.description ? true : false}
                                        {...field}
                                        onChange={(e) => handleTrimStartChange(e, field.onChange)}
                                    // error={errors?.description?.message ? errors?.description?.message.toString() : ''}
                                    />
                                );
                            }}
                        />
                    </div>

                    <div className={styles.fieldWrapperAppNickName}>
                        <Controller
                            name="buildingName"
                            control={control}
                            render={({ field }) => {
                                return (
                                    <TextInput
                                        label="Building Name"
                                        placeholder="Enter Building Name"
                                        // errorBorder={errors?.description ? true : false}
                                        {...field}
                                        value={field.value || ''}
                                        onChange={(e) => handleTrimStartChange(e, field.onChange)}
                                    // error={errors?.description?.message ? errors?.description?.message.toString() : ''}
                                    />
                                );
                            }}
                        />
                    </div>

                    <div className={styles.fieldWrapperAppNickName}>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => (
                                <TextArea
                                    label="Unit Description"
                                    isRequired={false}
                                    placeholder="Enter Unit Description"
                                    {...field}
                                    onChange={(e) => handleTrimStartChange(e, field.onChange)}
                                    error={errors?.description?.message?.toString() || ''}
                                />
                            )}
                        />
                    </div>

                    {unitData && <div className={styles.fieldWrapperUnitStatus}>
                        <label>Unit Status <span className={styles.astrikSign}>*</span></label>
                        <div className={styles.fieldWrapperValue}>
                            <Controller
                                name="unit_status"
                                control={control}
                                rules={{ required: 'Unit status is required' }}
                                render={({ field }) => (
                                    <Select
                                        className={styles.lockerSelect}
                                        placeholder="Select Unit Status"
                                        options={unitStatusOptions}
                                        isMulti={false}
                                        value={
                                            unitStatusOptions.find(
                                                (option) => option.value === field.value
                                            ) || null
                                        }
                                        onChange={(selected) =>
                                            field.onChange(selected ? selected.value : null)
                                        }
                                        styles={colourStyles}
                                        classNamePrefix="custom-react-select"
                                        maxMenuHeight={180}
                                    />
                                )}
                            />
                            {errors?.unit_status?.message && (
                                <p className={styles.errorText}>
                                    {errors.unit_status.message}
                                </p>
                            )}
                        </div>
                    </div>
                    }
                    <div className={styles.unitContainer}>
                        <div className={styles.unitWrapper}>
                            <label>
                                Size (Sq Ft) <span className={styles.astrikSign}>*</span>
                            </label>
                            <Controller
                                name="size"
                                control={control}
                                rules={{
                                    validate: (value) => value > 0 || 'Size not be 0',
                                }}
                                render={({ field }) => <Counter value={field.value ?? 0} setValue={field.onChange} name="size" />}
                            />
                            {errors?.size && <p className={styles.errorText}>{errors?.size ? errors?.size.message?.toString() : ''}</p>}
                        </div>
                        <div className={styles.unitWrapper}>
                            <label>Beds</label>
                            <Controller
                                name="beds"
                                control={control}
                                render={({ field }) => <Counter value={field.value} setValue={field.onChange} name="beds" />}
                            />
                        </div>
                        <div className={styles.unitWrapper}>
                            <label>
                                Baths <span className={styles.astrikSign}>*</span>
                            </label>
                            <Controller
                                name="baths"
                                control={control}
                                rules={{
                                    validate: (value) => value > 0 || 'baths size not be 0',
                                }}
                                render={({ field }) => <Counter value={field.value} setValue={field.onChange} name="baths" />}
                            />
                            {errors?.baths && <p className={styles.errorText}>{errors?.baths ? errors?.baths.message : ''}</p>}
                        </div>
                        <div className={styles.unitWrapper}>
                            <label>Price ($)</label>
                            <Controller
                                name="price"
                                control={control}
                                render={({ field }) => <Counter value={field.value} setValue={field.onChange} name="price" />}
                            />
                        </div>
                    </div>
                    <div className={styles.unitUploadContainer}>
                        <label>
                            Unit Pictures
                        </label>
                        <div className={styles.uploadWrapper}>
                            <Controller
                                name="uploadedImages"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        type="file"
                                        accept="image/jpeg, image/jpg, image/png, image/webp"
                                        multiple
                                        onChange={(e) => {
                                            const files = Array.from(e.target.files || []);
                                            const totalImages =
                                                files.length + getValues('existingImages')?.length + (field.value?.length || 0);

                                            if (totalImages > 10) {
                                                setShowToast?.(true);
                                                setToastMessage?.("Can't upload more than 10 files!");
                                                setTimeout(() => setShowToast?.(false), 4000);
                                                return;
                                            }

                                            field.onChange([...(field.value || []), ...files]);
                                        }}
                                    />
                                )}
                            />
                            <div className={styles.iconUnitUpload}>
                                <img src={iconUpload} alt="Icon Upload" />
                            </div>
                            {uploadedFiles.length === 0 && existingFiles.length === 0 && <p>Upload image from here</p>}

                            {/* Existing image URLs from backend */}
                            {existingFiles.length > 0 && (
                                <div className={styles.previewImgContainer}>
                                    {existingFiles.map((image: string, index: number) => (
                                        <div className={styles.imgWrapper} key={index}>
                                            <img className={styles.previewImg} src={image} alt="Preview Image" />
                                            <img
                                                className={styles.previewClose}
                                                onClick={() => {
                                                    const updated = [...existingFiles];
                                                    updated.splice(index, 1);
                                                    setValue('existingImages', updated);
                                                }}
                                                src={iconClosePreview}
                                                alt="Close Icon"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* New uploaded files (File objects) */}
                            {uploadedFiles.length > 0 && (
                                <div className={styles.previewImgContainer}>
                                    {uploadedFiles.map((file: File, index: number) => (
                                        <div className={styles.imgWrapper} key={index}>
                                            <img className={styles.previewImg} src={URL.createObjectURL(file)} alt="Preview Image" />
                                            <img
                                                className={styles.previewClose}
                                                onClick={() => {
                                                    const updated = [...uploadedFiles];
                                                    updated.splice(index, 1);
                                                    setValue('uploadedImages', updated);
                                                }}
                                                src={iconClosePreview}
                                                alt="Close Icon"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {errors?.uploadedImages && <p className={styles.errorText}>{errors?.uploadedImages?.message}</p>}
                    </div>
                    <div className={styles.btnUploadContainer}>
                        <button type="submit" className={`${styles.btnUpload} ${styles.success}`}>
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddUnits;
